import type { MajorStatus, MinorStatus } from "@citrine/types/status";

import type { StatusDetail } from "../common";
import type { IConstraint } from "../constraint";
import type { IDescriptor, IRealDescriptor } from "../descriptor";
import { getDescriptorKey, isValueDescriptor } from "../descriptor";
import type { SCORE_NAME_MAP } from "../execution-candidate";
import type { ResourceBase } from "../modules/common";
import type { IObjective } from "../objective";
import type {
  IChemicalFormulaFeaturizerPredictorConfig,
  IGeneralizedMeanPropertyPredictorConfig,
  IIngredientFractionsPredictorConfig,
  ILabelFractionsPredictorConfig,
  IMeanPropertyPredictorConfig,
  IMoleculeFeaturizerPredictorConfig,
  PredictorEntity,
} from "../predictor";
import {
  isExpressionOrLegacyExpressionPredictorConfig,
  isIngredientsToSimpleMixturePredictorConfig,
} from "../predictor";

export type ScoreType = keyof typeof SCORE_NAME_MAP;

export interface DesignWorkflowExecutionStickiedSelection {
  descriptors: string[];
  ingredients: Partial<Record<string, string[]>>;
}

export interface DesignWorkflowExecutionScore {
  name: string;
  description: string;
  objectives: IObjective[];
  constraints: IConstraint[];
  baselines?: number[];
  type: ScoreType;
}

export interface DesignWorkflowExecution extends Omit<ResourceBase, "status"> {
  id: string;
  workflow_id: string;
  score: DesignWorkflowExecutionScore;
  /**
   *  These are persisted when an execution is generated, however they don't
   *  accurately represent all of the descriptors in the execution results -
   *  for example SimpleML features are missing. This means that we can't trust
   *  this `descriptors` field to be complete and the only way (until we get
   *  asset versioning) to get the descriptors for a set of execution candidates
   *  is to scan over all of the candidates' properties.
   */
  descriptors: IDescriptor[];
  status?: MajorStatus;
  status_description?: MinorStatus;
  status_detail?: StatusDetail[];
  version_number?: number;
}

export interface IExecutionMetrics {
  x: number;
  y: number;
  value: number;
}

export const isDesignWorkflowExecutionFailed = (
  execution?: DesignWorkflowExecution,
): boolean => execution?.status === "FAILED";

export const isDesignWorkflowExecutionInProgress = (
  execution?: DesignWorkflowExecution,
) => execution?.status === "INPROGRESS";

export const isDesignWorkflowExecutionSucceeded = (
  execution?: DesignWorkflowExecution,
): boolean => execution?.status === "SUCCEEDED";

export const isDesignWorkflowExecutionFinished = (
  execution?: DesignWorkflowExecution,
): boolean =>
  isDesignWorkflowExecutionFailed(execution) ||
  isDesignWorkflowExecutionSucceeded(execution);

interface HasUpdateTime {
  update_time?: number;
}
export const isDesignWorkflowExecutionFresh = (
  execution: HasUpdateTime,
  workflow: HasUpdateTime,
): boolean =>
  Boolean(
    execution.update_time &&
    workflow.update_time &&
    new Date(execution.update_time) > new Date(workflow.update_time),
  );

export const isDesignWorkflowExecutionStale = (
  execution: HasUpdateTime,
  workflow: HasUpdateTime,
): boolean => !isDesignWorkflowExecutionFresh(execution, workflow);

export interface DesignWorkflowExecutionSummary {
  goals: string[];
  formulations: { key: string; labels: string[]; ingredients: string[] }[];
  /**
   * Note: includes IngredientsToSimpleMixture descriptors PLA-8222,
   *       missing design space descriptors used as expression inputs SPT-1627
   * */
  inputs: string[];
  /** Note: latent variables missing from outputs PLA-9083, PLA-9325 */
  outputs: string[];
  featurizers: {
    type: (
      | IChemicalFormulaFeaturizerPredictorConfig
      | IGeneralizedMeanPropertyPredictorConfig
      | IIngredientFractionsPredictorConfig
      | ILabelFractionsPredictorConfig
      | IMeanPropertyPredictorConfig
      | IMoleculeFeaturizerPredictorConfig
    )["type"];
    input: string;
    features: string[];
  }[];
  bounds: Partial<Record<string, { lower: number; upper: number }>>;
}

export const getNonFeaturizerRelatedExecutionInputs = (
  execution: DesignWorkflowExecution,
  executionSummary: DesignWorkflowExecutionSummary,
  predictor: PredictorEntity,
  removeIntermediateExpressionOutputs: boolean,
) => {
  const featurizerDescriptors = new Set(
    executionSummary.featurizers.flatMap(({ features }) => features),
  );
  // TODO: Remove this logic if the IngredientsToSimpleMixture predictor gets
  //       added to the execution summary and we no longer need to scan the
  //       predictor. PLA-8222
  const ingredientToSimpleMixtureDescriptors = new Set(
    predictor.data.instance.predictors
      .filter(isIngredientsToSimpleMixturePredictorConfig)
      .flatMap(({ id_to_quantity }) =>
        (Object.values(id_to_quantity) as IRealDescriptor[]).map(
          getDescriptorKey,
        ),
      ),
  );
  const primitiveDescriptorMap = Object.fromEntries(
    execution.descriptors.map((descriptor) => [
      descriptor.descriptor_key,
      isValueDescriptor(descriptor),
    ]),
  );

  // Execution summary object might be missing expression inputs, particularly if
  // they are descriptors that come from the design space
  // https://citrine.atlassian.net/browse/SPT-1627
  const inputKeys = new Set(executionSummary.inputs);
  const expressionPredictors = predictor.data.instance.predictors.filter(
    isExpressionOrLegacyExpressionPredictorConfig,
  );
  expressionPredictors.forEach(({ aliases }) => {
    Object.values(aliases).forEach((keyOrDescriptor: string | IDescriptor) => {
      const key =
        typeof keyOrDescriptor === "string"
          ? keyOrDescriptor
          : getDescriptorKey(keyOrDescriptor);
      if (!inputKeys.has(key)) {
        inputKeys.add(key);
      }
    });
  });

  // Expression predictor outputs are only classified as outputs in the
  // execution summary if they're actually outputs of the final model.
  // We want to eliminate them from the process parameters section of the
  // candidate show page and the experiment form. If PLA-9325 gets completed,
  // we can pull this information from the execution summary instead of
  // the predictor.
  const expressionOutputs = removeIntermediateExpressionOutputs
    ? expressionPredictors.map(({ output }) => output.descriptor_key)
    : [];
  return [...inputKeys].filter(
    (input) =>
      !featurizerDescriptors.has(input) &&
      !ingredientToSimpleMixtureDescriptors.has(input) &&
      primitiveDescriptorMap[input] &&
      !expressionOutputs.includes(input),
  );
};
